export type Nullish = null | undefined

export const toDictionary = <T extends Record<any, any>>(
  arr: T[],
  key: string | number
): Record<string, T> =>
  isArray(arr) ? Object.fromEntries(arr.map((v, i) => [v[key] || i, v])) : {}

export const isArray = Array.isArray

export const isEmpty = (v: unknown) => {
  if (isNullish(v)) return true
  if (isString(v)) return !/([^\s])/.test(v)
  if (isArray(v)) return v.length === 0
  if (isObject(v)) return isObjectEmpty(v)
  return false
}

export const isNullish = (v: unknown): v is Nullish =>
  v === undefined || v === null

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

export const isObject = (v: unknown): v is object =>
  !!v && typeof v === "object" && v.constructor === Object

const isTypeof =
  <T>(type: string) =>
  (v: unknown): v is T =>
    typeof v === type

export const isString = isTypeof<string>("string")

const isObjectEmpty = (obj: object) => {
  for (const _i in obj) return false
  return true
}

export const toArray = <T>(v: T) => (isArray(v) ? v : [v])

export const range = (length?: number) => Array.from(Array(length || 0).keys())

export const isBoolean = isTypeof<boolean>("boolean")

type ValueUnion<T> = T[keyof T]
export const match = <
  TValue extends string | number | symbol | undefined | null,
  TMatchObject extends
    | {
        [key in Exclude<TValue, undefined | null>]: any
      }
    | ({
        [key in Exclude<TValue, undefined | null>]?: any
      } & { default: Exclude<any, undefined> })
>(
  valueToMatch: TValue,
  matchObject: TMatchObject
): TMatchObject extends { default: Exclude<any, undefined> }
  ? ValueUnion<TMatchObject>
  : undefined | ValueUnion<TMatchObject> => {
  if (
    valueToMatch &&
    Object.prototype.hasOwnProperty.call(matchObject, valueToMatch)
  ) {
    return matchObject[valueToMatch as Exclude<TValue, undefined | null>]
  }
  //@ts-expect-error default is not in the type
  return matchObject["default"]
}

export const preventEventBubbling = (event: React.SyntheticEvent | Event) => {
  event.stopPropagation()
}

export const preventDefault = (event: React.SyntheticEvent | Event) => {
  event.preventDefault()
  preventEventBubbling(event)
}

export const preventDefaultWrapper =
  <E extends React.SyntheticEvent | Event = React.SyntheticEvent | Event>(
    fn?: any
  ) =>
  (event: E) => {
    event.preventDefault()
    preventEventBubbling(event)
    fn?.()
  }

type ParamShallowEqualObjects<T> = T | null | undefined
type TShallowEqualObjects = <T extends Record<string, unknown>>(
  a: ParamShallowEqualObjects<T>,
  b: ParamShallowEqualObjects<T>
) => boolean

export const shallowEqualObjects: TShallowEqualObjects = (a, b) => {
  if (a === b) return true
  if (!a || !b) return false

  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  const lenKeys = aKeys.length

  if (lenKeys !== bKeys.length) return false

  for (let i = 0; i < lenKeys; i++) {
    const key = aKeys[i]

    if (a[key] !== b[key]) {
      return false
    }
  }

  return true
}

export const generateExternalID = () => {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"
  const charactersLength = characters.length
  let result = ""

  for (let i = 0; i < 12; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength))
  }

  return { key: Date.now(), value: result.toUpperCase() }
}

export const isFirefox = () => {
  if (typeof navigator === "undefined") return false

  return navigator.userAgent.toLowerCase().includes("firefox")
}

export const isModifierKey = (event: KeyboardEvent) =>
  event.metaKey || event.ctrlKey

export const isCmdEnter = (event: KeyboardEvent) =>
  isModifierKey(event) && event.key === "Enter"

export const isSubmitAllowedOnElement = (
  event: KeyboardEvent,
  element: Element | null = document.activeElement
) => {
  if (!element) return false
  if (!isCmdEnter(event)) return false

  const tag = element.tagName
  const isInput = tag === "INPUT"
  const isTextArea = tag === "TEXTAREA"
  const isDiv = tag === "DIV"
  const isLexicalEditor =
    isDiv && element.getAttribute("data-lexical-editor") === "true"

  const allowed = isInput || isTextArea || isLexicalEditor

  if (allowed) preventDefault(event)

  return element
}

const hasUrlProtocol = (value: string | undefined, protocols: string[]) => {
  try {
    return protocols.includes(new URL(value ?? "").protocol)
  } catch {
    return false
  }
}

// Stored URLs (e.g. a CI job URL) can be anything, including "javascript:".
export const isHttpUrl = (value?: string) =>
  hasUrlProtocol(value, ["http:", "https:"])

export const isHttpsUrl = (value?: string) => hasUrlProtocol(value, ["https:"])
